// utils/logger.ts
import pino from "pino";

// Define base log level from environment or default to 'info'
const LOG_LEVEL = process.env.LOGGING_LEVEL || 'info';

// Use pino-pretty for human-readable logs in development
const isProduction = process.env.NODE_ENV === 'production';

const sanitizeLogString = (value: string): string => {
  return value
    .replace(/[\r\n]+/g, ' ')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]+/g, '');
};

const sanitizeLogValue = (value: unknown, seen = new WeakSet<object>()): unknown => {
  if (typeof value === "string") {
    return sanitizeLogString(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeLogValue(item, seen));
  }

  if (value && typeof value === "object") {
    if (seen.has(value as object)) {
      return "[Circular]";
    }

    seen.add(value as object);

    if (value instanceof Error) {
      return {
        name: sanitizeLogString(value.name),
        message: sanitizeLogString(value.message),
        stack: value.stack ? sanitizeLogString(value.stack) : undefined,
      };
    }

    const sanitizedObject: Record<string, unknown> = {};
    for (const [key, nestedValue] of Object.entries(value as Record<string, unknown>)) {
      sanitizedObject[key] = sanitizeLogValue(nestedValue, seen);
    }
    return sanitizedObject;
  }

  return value;
};

// Create the base logger
const baseLogger = pino(
  isProduction
    ? {
        level: LOG_LEVEL,
        timestamp: pino.stdTimeFunctions.isoTime,
        formatters: {
          level(label) {
            return { level: label };
          },
        },
        hooks: {
          logMethod(inputArgs, method) {
            const sanitizedArgs = inputArgs.map((arg) => sanitizeLogValue(arg));
            method.apply(this, sanitizedArgs as Parameters<typeof method>);
          },
        },
      }
    : {
        level: LOG_LEVEL,
        timestamp: pino.stdTimeFunctions.isoTime,
        formatters: {
          level(label) {
            return { level: label };
          },
        },
        hooks: {
          logMethod(inputArgs, method) {
            const sanitizedArgs = inputArgs.map((arg) => sanitizeLogValue(arg));
            method.apply(this, sanitizedArgs as Parameters<typeof method>);
          },
        },
        transport: {
          target: 'pino-pretty',
          options: { colorize: true }
        }
      }
);

/**
 * Creates a named logger with consistent configuration
 * @param serviceName The service/component name for the logger
 * @param level Optional override for the log level
 */
const createLogger = (serviceName: string, level = LOG_LEVEL) => {
  return baseLogger.child({
    name: serviceName,
    level
  });
};

// Service loggers
export const aboutPremiumPageLogger = createLogger("AboutPremiumPage");
export const accessServiceLogger = createLogger("AccessService");
export const apiUtilLogger = createLogger("ApiUtil");
export const authLogger = createLogger("Auth");
export const authServiceLogger = createLogger("AuthService");
export const caseServiceLogger = createLogger("CaseService");
export const commCenterServiceLogger = createLogger("CommCenterService");
export const contentServiceLogger = createLogger("ContentService");
export const costPatientServiceLogger = createLogger("CostPatientService");
export const costServiceLogger = createLogger("CostService");
export const delegateServiceLogger = createLogger("DelegateService");
export const groupServiceLogger = createLogger("GroupService");
export const jwtUtilsLogger = createLogger("jwtUtils");
export const middlewareLogger = createLogger("middleware");
export const physicianServiceLogger = createLogger("PhysicianService");
export const qualityServiceLogger = createLogger("QualityService");
export const secureServiceLogger = createLogger("SecureService");
export const securityServiceLogger = createLogger("SecurityService");
export const userServiceLogger = createLogger("UserService");
export const routeLogger = createLogger("RouteLogger");
export { sanitizeLogString, sanitizeLogValue };

// This logger is intended for use ONLY in local development
// and should not be used in production environments
export const localDevelopmentAuthLogger = process.env.NODE_ENV === "development"
  ? createLogger("LocalDevelopmentAuth")
  : undefined;

/**
 * API Loggers - used for logging API operations
 */
export const designationApiLogger = createLogger("DesignationApi");
export const qualityApiLogger = createLogger("QualityApi");

/**
 * Extended logger type with custom methods for API logging
 */
type ExtendedLogger = pino.Logger & {
  logEndpoint?: (endpoint: string, context?: Record<string, any>) => void;
  logApiError?: (endpoint: string, error: any, context?: Record<string, any>) => void;
};

/**
 * Adds API logging helper methods to a logger
 * @param logger The logger to extend with API methods
 * @returns The extended logger
 */
function extendLoggerWithApiMethods(logger: pino.Logger): ExtendedLogger {
  const extendedLogger = logger as ExtendedLogger;

  extendedLogger.logEndpoint = (endpoint: string, context = {}) => {
    extendedLogger.debug({
      msg: `Using endpoint: ${endpoint}`,
      endpoint,
      ...context
    });
  };

  extendedLogger.logApiError = (endpoint: string, error: any, context = {}) => {
    extendedLogger.error({
      msg: `API Error: ${error?.message || error}`,
      endpoint,
      statusCode: error?.response?.status,
      statusText: error?.response?.statusText,
      ...context
    });
  };

  return extendedLogger;
}

// Extend API loggers with helper methods
extendLoggerWithApiMethods(designationApiLogger);
extendLoggerWithApiMethods(qualityApiLogger);
